import { useWorkspace } from '@/features/workspaces/hooks';
import { ConnectorFlowDTO, ConnectorInstanceSafeDTO, CustomConnectorDto, GupshupOnboardingDTO } from '@/models/connectors';
import { useCompanyValues } from '@/providers';
import {
  useGetConnectorFlowsQuery,
  useGetConnectorInstancesQuery,
  useGetGupshupOnboardingQuery,
  useGetWorkspaceConnectorsQuery,
} from '@/services/endpoints/workspace-connectors';
import { WHATSAPP_BUSINESS_CONNECTOR_NAME, WHATSAPP_CONNECTOR_NAME } from './constants';

type ResolveWhatsappChannelArgs = {
  connectors?: CustomConnectorDto[];
  instances?: ConnectorInstanceSafeDTO[];
  onboarding?: GupshupOnboardingDTO;
};

/**
 * Names of the flow serving each role, per connector.
 *
 * Both WhatsApp connectors declare the same four flows, and their display names are not unique
 * across the platform — mailtrap, sms and facebook all ship a flow called "Send" too. So a flow is
 * only ever looked up *within* the connector serving the channel, never by name alone.
 *
 * Two names per role because the connectors were seeded at different times: rows created before
 * the flows were renamed still carry the long form, and connector discovery does not rewrite the
 * name of a flow it has already inserted.
 */
export const WHATSAPP_FLOW_NAMES = {
  send: ['Whatsapp Send Flow', 'Send'],
  templateInsert: ['Whatsapp Template Insert Flow', 'Insert Template'],
  templateEdit: ['Whatsapp Template Edit Flow', 'Edit Template'],
  templateDelete: ['Whatsapp Template Delete Flow', 'Delete Template'],
} as const;

export type WhatsappFlowRole = keyof typeof WHATSAPP_FLOW_NAMES;

export const resolveWhatsappFlow = (flows: ConnectorFlowDTO[] | undefined, connectorId: string | undefined, role: WhatsappFlowRole) => {
  if (!connectorId) {
    return undefined;
  }
  const names: readonly string[] = WHATSAPP_FLOW_NAMES[role];
  return flows?.find((flow) => flow.connectorId === connectorId && names.includes(flow.name));
};

/**
 * Works out which connector backs the WhatsApp channel, and whether it can actually send.
 *
 * When both are connected the Meta one wins, so workspaces already sending are left alone.
 */
export const resolveWhatsappChannel = ({ connectors, instances, onboarding }: ResolveWhatsappChannelArgs) => {
  const metaConnector = connectors?.find((connector) => connector.name === WHATSAPP_CONNECTOR_NAME);
  const businessConnector = connectors?.find((connector) => connector.name === WHATSAPP_BUSINESS_CONNECTOR_NAME);

  const isMetaConnected = metaConnector?.isAuthenticated ?? false;
  const isBusinessConnected = businessConnector?.isAuthenticated ?? false;
  const usesBusiness = !isMetaConnected && isBusinessConnected;
  const connector = isMetaConnected ? metaConnector : businessConnector;

  return {
    connector,
    isAuthenticated: isMetaConnected || isBusinessConnected,
    usesBusiness,
    instanceId: instances?.find((instance) => instance.connectorId === connector?.id)?.id,
    businessInstanceId: instances?.find((instance) => instance.connectorId === businessConnector?.id)?.id,
    onboarding,
    /**
     * Connected through Whatsapp Business, but no phone number attached yet. Stays false while the
     * onboarding state is still unknown, so a slow answer does not flash the activation screen at
     * a workspace that is already live.
     */
    needsOnboarding: usesBusiness && onboarding != null && !onboarding.live,
  };
};

/**
 * Resolves which connector backs the WhatsApp channel for this workspace.
 *
 * Two connectors can serve it: the original one talking to Meta directly, and "Whatsapp Business",
 * which goes through Gupshup as BSP. They differ in one way that matters to the UI — a Whatsapp
 * Business instance exists as soon as it is connected, but it cannot send until the customer has
 * attached their own phone number through Meta's embedded signup. Until that happens the workspace
 * has a connector and no number, and the only thing worth showing is the link that fixes it.
 */
export const useWhatsappChannelConnector = () => {
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();

  const { data: connectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );

  const { data: instances, isLoading: isInstancesLoading } = useGetConnectorInstancesQuery({ workspaceId: workspace.id }, { skip: !workspace.id });

  const { usesBusiness, businessInstanceId } = resolveWhatsappChannel({ connectors, instances });

  // Gupshup-specific, and 404s on any other connector instance — so it is only asked for once we
  // know the workspace really is served by Whatsapp Business.
  const { data: onboarding, isLoading: isOnboardingLoading } = useGetGupshupOnboardingQuery(
    { workspaceId: workspace.id, connectorInstanceId: businessInstanceId ?? '' },
    { skip: !workspace.id || !usesBusiness || !businessInstanceId }
  );

  return {
    ...resolveWhatsappChannel({ connectors, instances, onboarding }),
    isLoading: isConnectorsLoading || isInstancesLoading || isOnboardingLoading,
  };
};

/**
 * The flow serving one role on whichever connector backs the channel, plus the instance to run it
 * against. Screens driving a WhatsApp flow use this instead of hunting for a flow by name.
 */
export const useWhatsappChannelFlow = (role: WhatsappFlowRole) => {
  const workspace = useWorkspace();
  const channel = useWhatsappChannelConnector();

  const { data: flows, isLoading: isFlowsLoading } = useGetConnectorFlowsQuery(
    { workspaceId: workspace.id, queryParams: { showUserJourney: true } },
    { skip: !workspace.id }
  );

  const flow = resolveWhatsappFlow(flows, channel.connector?.id, role);

  return {
    ...channel,
    flow,
    connectorInstanceId: channel.instanceId,
    isLoading: channel.isLoading || isFlowsLoading,
  };
};
